/**
 * EmployeeTimelineView.tsx
 * Resource Timeline: employees as treegrid rows (tier → employee → bars) on a
 * continuous time axis, via the licensed Highcharts ScheduleGantt (reused
 * unchanged). Click a bar → detail drawer. Drag is intentionally not done
 * in-chart (Highcharts wrapper forbids chart.update) — reassign lives in the
 * drawer + the Weekly Board.
 */

import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import type Highcharts from 'highcharts';
import ScheduleGantt from '../../../components/schedule/ScheduleGantt';
import { buildEmployeeGanttData, employeeGanttRowCount } from '../../../components/schedule/employeeGanttRowBuilder';
import { buildTierGroups } from '../../../components/schedule/employeeScheduleShared';
import type { EmployeeBar, EmployeeBarUser } from '../../../services/scheduleEmployeeBars';

export interface EmployeeTimelineViewProps {
  users: EmployeeBarUser[];
  bars: EmployeeBar[];
  weekStarts?: string[];
  windowStart: string;
  windowEnd: string;
  onBarClick: (bar: EmployeeBar) => void;
}

export const EmployeeTimelineView: React.FC<EmployeeTimelineViewProps> = ({
  users, bars, windowStart, windowEnd, onBarClick,
}) => {
  const theme = useTheme();
  const groups = useMemo(() => buildTierGroups(users), [users]);
  const barsByUser = useMemo(() => {
    const m = new Map<string, EmployeeBar[]>();
    for (const b of bars) { const a = m.get(b.userKey) ?? []; a.push(b); m.set(b.userKey, a); }
    return m;
  }, [bars]);
  const series = useMemo(() => buildEmployeeGanttData(groups, barsByUser), [groups, barsByUser]);
  const height = useMemo(() => Math.max(240, employeeGanttRowCount(groups, barsByUser) * 26 + 120), [groups, barsByUser]);
  const barByKey = useMemo(() => {
    const m = new Map<string, EmployeeBar>();
    for (const b of bars) m.set(b.assignmentKey.toLowerCase(), b);
    return m;
  }, [bars]);

  const handlePointClick = (point: Highcharts.Point) => {
    const custom = (point.options as { custom?: { assignmentKey?: string } }).custom;
    if (custom?.assignmentKey) {
      const b = barByKey.get(String(custom.assignmentKey).toLowerCase());
      if (b) onBarClick(b);
    }
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <ScheduleGantt
        series={series}
        startDate={windowStart}
        endDate={windowEnd}
        height={height}
        onPointClick={handlePointClick}
        conflictColors={{ hard: theme.schedule.conflictHard, soft: theme.schedule.conflictSoft }}
      />
    </Box>
  );
};
